import * as THREE from 'three'

/**
 * 场景灯光：给建筑白模四周布一圈光，消除外立面「背光面发黑」。
 * * ⚠️ 坐标系铁律（本项目最容易踩的一处）
 *   customCoords 的 three 世界是 **X = 东 / Y = 北 / Z = 上**（Z-up），
 *   不是 three 默认的 Y-up。**灯的"高度"必须写进 z 分量。**
 *
 *   旧代码写的是 `dir.position.set(500, 400, 800)` —— 那是按 Y-up 的直觉写的
 *   （x=东 500、y=高度 400、z=南 800）。落到 Z-up 世界里就变成了
 *   「向东 500m、向北 400m、高 800m」，方向完全变了；更糟的是 y=400 被解释成
 *   "向北 400m"，主光实际是以接近平角从北侧横着照过来，朝北/朝东的立面几乎收不到光，
 *   于是那几面就一直发黑。这里全部改成 (东, 北, 上) 的明确写法。
 *
 * 布光思路（"四周一圈"）
 *   楼是竖直体块，外立面的明暗只取决于**水平方位的来光**，与光源高度关系不大。
 *   所以真正有效的做法不是把灯堆高，而是把方位铺满 —— 每 120° 放一盏等高平行光，
 *   三盏互相补位，任何一面至少能收到两盏灯（一盏正面、一盏掠射），不会再出现纯黑面。
 *   另外 DirectionalLight 的光只跟方向有关、与位置距离无关，这里只用方位角，
 *   不设 target：平行光默认朝原点照射 == 朝模型并集包围盒中心照射（模型中心正好钉在原点）。
 */

/**
 * 夜景模式总开关：切换昼夜两套灯光预设（真开关改成 false 即回白天）。
 * 它同时决定 sceneView.js 里的清屏色与雾色（CLEAR_COLOR / FOG_COLOR 都读 NIGHT_MODE），
 * 改这里两边会一起变 —— 底图拆掉之后，"夜色"完全靠这三者一致才成立。
 * 夜景思路（对照参考效果图「蓝调时刻」）：整体压暗 + 冷蓝月光主调 + 一盏暖色
 * 城市光点缀，配合 Bloom/雾效出氛围；环境光保底防黑面。
 */
export const NIGHT_MODE = true

/** 灯光总开关（想在控制台快速对比明暗效果时改成 false 即可） */
export const LIGHTS_ENABLED = true

/**
 * 环境光：均匀提亮所有朝向的面，是"没有黑面"的最后一道保险。
 * 白天 0.95 白光 ≈ 贡献 28% 照度；夜里换深蓝低强度 —— 够兜住轮廓，
 * 又不会把夜景提亮成"灰昼"。
 */
const AMBIENT_DAY = { color: 0xffffff, intensity: 0.95 }
// 夜景环境光：0.85 那档在"深绿地台"时代实测会把画面糊成薄荷绿（暗反照率被推到接近
// 饱和后 ACES 一去饱和就发白）。2026-10-22 地台已换成浅灰铺装，那条约束解除；
// 0.85 实测视口平均亮度 97→126、过曝 0.53%，g−r / b−g 比值没动。
const AMBIENT_NIGHT = { color: 0x3b4a5e, intensity: 0.85 }
export const AMBIENT = NIGHT_MODE ? AMBIENT_NIGHT : AMBIENT_DAY
export const AMBIENT_INTENSITY = AMBIENT.intensity // 兼容旧引用

/**
 * 半球光：天空色 / 地面色，给竖直外立面一个柔和的上下渐变。
 * 白天：天光冷白 + 地面反光暖灰；夜里：深蓝天穹 + 近黑的地面，
 * 强度同步压低，让立面只留一点冷色底光。
 */
const HEMI_DAY = { sky: 0xf2f7ff, ground: 0xa8b4c0, intensity: 1.5 }
// 天光偏青蓝（天空余晖）；**地面色不再给近黑**：0x1a2418 等于"地面不反弹任何光"，
// 楼的竖直面下半段就没有来光，看着像插在黑洞里。抬到 0x2c3626 给一道地面反弹，
// 这是"质感"里最便宜的一项（同时它也是绿地变亮的通道，别再加）。
const HEMI_NIGHT = { sky: 0x36495e, ground: 0x2c3626, intensity: 1.2 }
export const HEMI = NIGHT_MODE ? HEMI_NIGHT : HEMI_DAY
export const HEMI_SKY = HEMI.sky // 兼容旧引用
export const HEMI_GROUND = HEMI.ground
export const HEMI_INTENSITY = HEMI.intensity

/**
 * 渲染曝光。参考效果图是**高调（bright）**画面，我们默认值一路偏暗：
 * 1.55 时整幅平均色只有 (80,100,96) ≈ 38% 灰，画面读起来闷。
 * ⚠️ 这个数不能单独看：辉光是一层"摊开的加法亮度"，把 BLOOM_STRENGTH/RADIUS 收小
 * 会连带把中间调压下去（实测 1.70 + Bloom 0.7/0.35 只比 1.55 + 0.9/0.5 亮 3/255）。
 * 所以"亮"主要靠这里，"干净"靠 Bloom —— 两边要一起定。
 * 2026-10-22 整体调亮：1.75 → 2.05（同机位视口平均亮度 97 → 126，过曝 0.12% → 0.53%）。
 * 2026-10-22 再提一档 2.05 → 2.45（同页配对扫过 2.05/2.25/2.45/2.70：视口平均 146.7 → 156.8，
 * 过曝 0.51% → 0.62%，b−r 全程 11.7 没塌 —— 说明这一档还没吃到 ACES 的去饱和拐点）。
 */
export const TONE_MAPPING_EXPOSURE = NIGHT_MODE ? 2.45 : 1.22

/**
 * 夕照侧那盏主光（rimSW）的颜色。单独导出是因为**水面的镜面高光代表同一盏光**：
 * environment.js 的 WATER_SUN_DIR 已经按 rimSW 的方位/俯仰硬编码了方向，颜色也必须同源，
 * 否则楼是冷白、河上却拖一道金光。
 */
export const SUN_KEY_COLOR_NIGHT = 0xdceef8 // 冷白
// export const SUN_KEY_COLOR_NIGHT = 0xffe4b5    // 淡黄

/**
 * 四周一圈主光。每项：
 *   az   —— 方位角（度）：0 = 正北（+Y），90 = 正东（+X），180 = 正南，270 = 正西
 *   el   —— 俯仰角（度）：0 = 平射（纯侧光，立面明暗对比最强），90 = 正上方垂直下射
 *   h    —— 绝对高度（米），只作量纲参照（平行光与距离无关），取模型最高点量级
 *   intensity / color —— 光强与色温
 *
 * 三盏主光的方位间隔 120°，保证"楼的任何一面都至少被两盏照到"。俯仰都压在 22~28°，
 * 属于低平射 —— 立面收光效率最高（cos 入射角大），而且不会出现"照得亮屋顶、立面还是暗"的情况。
 */
const RIM_LIGHTS_DAY = [
  // 从东北侧来光：照亮东立面和北立面（原来最暗的两个朝向）
  { name: 'rimNE', az: 52, el: 24, h: 520, color: 0xcfe6ff, intensity: 10.85 },
  // 从西南侧来光：与上面成 120°+，照亮西立面和南立面
  { name: 'rimSW', az: 208, el: 24, h: 520, color: 0xc2ddff, intensity: 10.7 },
  // 从西北侧来光：补北立面与西立面之间的过渡
  { name: 'rimNW', az: 320, el: 26, h: 460, color: 0xb4d4ff, intensity: 10.5 },
]
const RIM_LIGHTS_NIGHT = [
  // 冷色月光（东北来光）：照亮东/北立面，压住冷调。
  // ⚠️ 三盏冷光的色相一律走"青蓝"而不是"紫蓝"：0x9cb8e8 是 B-G(48) > G-R(28)，
  // 打在浅蓝幕墙上就是用户否掉的"蓝紫"。现在 G 稳稳落在 R、B 中间。
  // { name: 'rimNE', az: 52, el: 24, h: 520, color: 0x9ec2e4, intensity: 3.8 },
  { name: 'rimNE', az: 52, el: 24, h: 520, color: 0x89B6E0, intensity: 3.8 },
  // 主光（西南来光）：2026-10-22 用户要求从暖金改成**冷白**。
  // 0xdceef8 的 g−r(18) > b−g(10) —— 冷白但不带紫；比另两盏冷光（0x9ec2e4/0x8cb2d8）白得多，
  // 所以它仍然读得出"主光"而不是"第四盏补光"。副作用：同一 intensity 下它的亮度权重
  // 比暖金高约 20%（0xffd9b3 的 0.2126R+0.7152G+0.0722B = 0.74 → 0.90），画面会再亮一点。
  // ⚠️ 水面的镜面高光吃的是同一个颜色（environment.js 引这个常量），别再单独调那边。
  // { name: 'rimSW', az: 208, el: 24, h: 520, color: SUN_KEY_COLOR_NIGHT, intensity: 5.6 },
  { name: 'rimSW', az: 208, el: 24, h: 520, color: 0xC8EBFF, intensity: 5.6 },
  // 冷蓝补光（西北来光）：填背光面，保持冷暖对比
  // { name: 'rimNW', az: 320, el: 26, h: 460, color: 0x8cb2d8, intensity: 3.0 },
  { name: 'rimNW', az: 320, el: 26, h: 460, color: 0x6EA3D8, intensity: 3.0 },
]
export const RIM_LIGHTS = NIGHT_MODE ? RIM_LIGHTS_NIGHT : RIM_LIGHTS_DAY

/** 把方位角/俯仰角/高度换成 Z-up 世界里的位置向量 */
function positionOf(az, el, h) {
  const a = (az * Math.PI) / 180
  const e = (el * Math.PI) / 180
  const horiz = h * Math.cos(e)
  return [horiz * Math.sin(a), horiz * Math.cos(a), h * Math.sin(e)]
}

/**
 * 真阴影（用户 2026-10-22："中心 21 幢建筑物…再加一些阴影效果进去，使之增强一些立体感"）。
 *
 * **只给主光 rimSW 挂 shadow map。** 三盏都投影反而看不出阴影：这套布光的立身之本是
 * "任何一面至少被两盏照到"（见上面 RIM_LIGHTS 的注释），三盏都挡就等于把楼间全部糊黑；
 * 只挡一盏，被挡的那面掉一半亮度，方向感才读得出来。
 *
 * ⚠️ 场景和灯都是静态的，所以 renderer.shadowMap.autoUpdate 关掉、进场补渲一次就够。
 * 这条不是微优化：本项目一帧里 renderer.render(scene) 会被水面反射再调一次
 * （见 environment.js 的 waterHub），autoUpdate 开着等于每帧多渲两遍全场景深度。
 *
 * normalBias 给到 1.2m ≈ 1.5 个 texel（正交盒约 1.7km / 2048 ≈ 0.82m/格）：这批楼是
 * 照片贴图的实测模型，表面起伏比 texel 还大，不给 normalBias 就是满墙麻点。
 * bias 留 0 —— 平行光阴影的 bias 单位是"归一化深度"，这套 near/far 跨度约 3.4km，
 * -0.0004 就是 1.4m 的假偏移，楼脚会直接飘起来。
 */
export const SHADOWS = {
  enabled: true,
  caster: 'rimSW',
  mapSize: 2048,
  normalBias: 1.2,
  bias: 0,
  /** 正交盒在楼群包围盒之外再放多少米：el 24° 下 100m 高的楼要拖 224m 影长 */
  margin: 260,
}

/**
 * 把某盏平行光的正交阴影相机贴合到一个世界包围盒上（Z-up：X=东 / Y=北 / Z=上）。
 * 同时把灯沿**原方向**推到盒外并把 target 挪到盒心 —— 方位角/俯仰角不变，所以立面
 * 的受光关系一点没动，动的只是"阴影视锥罩住哪一块"。
 * @returns {{side:number, texel:number}} 正交盒边长与一个 texel 多少米（自检用）
 */
export function fitShadowCamera(light, box, margin = SHADOWS.margin) {
  const center = box.getCenter(new THREE.Vector3())
  const size = box.getSize(new THREE.Vector3())
  // 正方形：水平要罩住"楼群 + 影长"，竖直方向因为光是斜着下来的，同样要这个量级
  const side = Math.max(size.x, size.y) + margin * 2
  const half = side / 2
  const dir = light.position.clone()
  if (dir.lengthSq() < 1e-6) dir.set(0, 0, 1)
  dir.normalize()
  const dist = half + size.z + margin
  light.position.copy(center).addScaledVector(dir, dist)
  // target 不进场景图：这样每帧 scene.updateMatrixWorld() 不会碰它，这里手动算一次就一直在位
  light.target.position.copy(center)
  light.target.updateMatrixWorld(true)
  const cam = light.shadow.camera
  cam.up.set(0, 0, 1) // 与主相机同一套 Z-up，阴影相机的滚转才和画面里的"北"对得上
  cam.left = -half
  cam.right = half
  cam.top = half
  cam.bottom = -half
  cam.near = 1
  cam.far = dist * 2 + side
  cam.updateProjectionMatrix()
  light.shadow.mapSize.set(SHADOWS.mapSize, SHADOWS.mapSize)
  light.shadow.normalBias = SHADOWS.normalBias
  light.shadow.bias = SHADOWS.bias
  return { side, texel: side / light.shadow.mapSize.x }
}


/**
 * 往场景里加灯，返回灯光数组（便于运行时调参 / 调试）。
 * @param {THREE.Scene} scene
 * @returns {THREE.Object3D[]}
 */
export function createLights(scene) {
  if (!LIGHTS_ENABLED) return []

  const lights = []

  const amb = new THREE.AmbientLight(AMBIENT.color, AMBIENT.intensity)
  amb.name = 'ambient'
  lights.push(amb)

  const hemi = new THREE.HemisphereLight(HEMI.sky, HEMI.ground, HEMI.intensity)
  hemi.name = 'hemi'
  lights.push(hemi)

  RIM_LIGHTS.forEach((cfg) => {
    const l = new THREE.DirectionalLight(cfg.color, cfg.intensity)
    const p = positionOf(cfg.az, cfg.el, cfg.h)
    l.position.set(p[0], p[1], p[2])
    l.name = cfg.name
    l.userData.az = cfg.az
    l.userData.el = cfg.el
    if (SHADOWS.enabled && cfg.name === SHADOWS.caster) {
      l.castShadow = true
      // 正交盒的边长/近远平面要等楼群包围盒算出来才能定 → sceneView 里 fitShadowCamera()
      l.shadow.camera.name = `shadow_${cfg.name}`
    }
    lights.push(l)
  })

  lights.forEach((l) => scene.add(l))
  return lights
}
